import { ReactNode, useEffect, useRef, useState } from 'react';
import Mark from 'mark.js';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import VideoGenericElement from '$components/GenericElements/VideoGenericElement';
import getUserType from '$utils/getUserType';
import { UserTypes } from '$types/UserTypes';

export type SmartInterviewVideoModalVideo = {
  Id: number;
  Url: string;
  SmartInterviewQuestions?: { Id: number; Name: string } | null;
  Transcription?: Record<string, unknown> | null;
};

type TranscriptSegment = {
  text: string;
  startMs?: number;
};

function getTranscriptSegments(
  transcription: Record<string, unknown>
): TranscriptSegment[] {
  const utterances = transcription.utterances as
    Array<{ speaker: string; text: string; start_ms: number }> | undefined;
  if (Array.isArray(utterances) && utterances.length > 0) {
    return utterances.map((u) => ({ text: u.text, startMs: u.start_ms }));
  }

  const results = transcription.results as Record<string, unknown> | undefined;
  if (results) {
    const transcripts = results.transcripts as
      Array<{ transcript: string }> | undefined;
    if (Array.isArray(transcripts) && transcripts.length > 0) {
      return transcripts.map((t) => ({ text: t.transcript }));
    }
  }

  const segments = transcription.segments as
    Array<Record<string, unknown>> | undefined;
  if (Array.isArray(segments)) {
    return segments.map((s) => ({
      text: String(s.text ?? s.content ?? JSON.stringify(s)),
      startMs: s.start_ms as number | undefined,
    }));
  }

  if (Array.isArray(transcription)) {
    return (transcription as unknown[]).map((item) => {
      if (typeof item === 'string') return { text: item };
      if (item && typeof item === 'object') {
        const obj = item as Record<string, unknown>;
        return {
          text: String(
            obj.text ?? obj.content ?? obj.transcript ?? JSON.stringify(obj)
          ),
          startMs: obj.start_ms as number | undefined,
        };
      }
      return { text: String(item) };
    });
  }

  return [{ text: JSON.stringify(transcription, null, 2) }];
}

function formatTime(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = String(totalSec % 60).padStart(2, '0');
  return `${min}:${sec}`;
}

const SmartInterviewVideoModal = ({
  video,
  footer,
}: {
  video?: SmartInterviewVideoModalVideo;
  footer?: ReactNode;
}) => {
  const userType = getUserType();
  const isInternal = userType === UserTypes.INTERNAL;
  const highlightClass = isInternal
    ? 'bg-internal-main/20'
    : 'bg-employer-primary/20';
  const activeHighlightClass = isInternal
    ? 'bg-internal-main/50'
    : 'bg-employer-primary/50';

  const searchableContent = useRef<HTMLDivElement | null>(null);
  const markInstance = useRef<Mark | null>(null);
  const markIndex = useRef(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const scrollMatchIntoView = () => {
    const previousSearchHighlight = document.querySelector(
      '.currentMatchedMark'
    );
    if (previousSearchHighlight) {
      previousSearchHighlight.classList.remove(
        activeHighlightClass,
        'currentMatchedMark'
      );
      previousSearchHighlight.classList.add(highlightClass);
    }

    const markElements = document.querySelectorAll('mark');
    let matchedIndex;
    if (markIndex.current < markElements.length) {
      matchedIndex = markIndex.current;
    } else {
      matchedIndex = 0;
    }

    const matchedIndexElement = markElements[matchedIndex];
    if (!matchedIndexElement) return;

    matchedIndexElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
    markIndex.current = matchedIndex + 1;

    matchedIndexElement.classList.remove(highlightClass);
    matchedIndexElement.classList.add(
      activeHighlightClass,
      'currentMatchedMark'
    );
  };

  const updateFades = () => {
    const el = searchableContent.current;
    if (!el) return;
    setShowTopFade(el.scrollTop > 0);
    setShowBottomFade(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  };

  useEffect(() => {
    if (searchableContent.current) {
      if (markInstance.current) markInstance.current.unmark();
      else markInstance.current = new Mark(searchableContent.current);

      if (searchTerm) {
        markInstance.current.mark(searchTerm, {
          className: highlightClass,
          separateWordSearch: false,
          acrossElements: true,
          ignoreJoiners: true,
          done: (totalMatches) => {
            if (totalMatches > 0) {
              markIndex.current = 0;
              scrollMatchIntoView();
            }
          },
        });
      }
    }

    return () => {
      if (markInstance.current) markInstance.current.unmark();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchableContent, markInstance, searchTerm]);

  useEffect(() => {
    updateFades();
  }, [video]);

  const transcriptionUtterances = video?.Transcription?.utterances as
    unknown[] | undefined;
  const hasValidTranscript =
    Array.isArray(transcriptionUtterances) &&
    transcriptionUtterances.length > 1;
  const transcriptSegments =
    hasValidTranscript && video?.Transcription
      ? getTranscriptSegments(video.Transcription)
      : [];

  const handleSeek = (startMs: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = startMs / 1000;
      videoRef.current.play().catch(() => {});
    }
  };

  return (
    <>
      <div className="mb-4 text-spk-highContrast text-base font-bold">
        {video?.SmartInterviewQuestions?.Name}
      </div>
      <div className="w-full items-start justify-center flex gap-4">
        <VideoGenericElement
          videoData={{
            Name: video?.SmartInterviewQuestions?.Name as string,
            url: video?.Url,
          }}
          videoRef={videoRef}
          controls
          className="max-w-2xl max-h-2xl w-auto rounded-xl"
        />
        {hasValidTranscript && (
          <div className="flex flex-col min-w-0 flex-1">
            <IconField iconPosition="left" className="w-full relative">
              <InputIcon className="pi pi-search" />
              <InputText
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type to search..."
                className="w-full"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    scrollMatchIntoView();
                  }
                }}
              />
              <button
                type="button"
                className="absolute h-full right-0 text-sm border-0 border-l border-l-spk-neutral-40 border-solid px-2 disabled:cursor-not-allowed disabled:text-black/20"
                disabled={searchTerm.length === 0}
                onClick={scrollMatchIntoView}
              >
                Next <i className="pi pi-arrow-right text-xs" />
              </button>
            </IconField>

            <div className="relative mt-8 max-h-96 max-w-2xl">
              {showTopFade && (
                <div className="pointer-events-none absolute top-0 left-0 right-0 h-10 z-10 bg-gradient-to-b from-white to-transparent" />
              )}
              <div
                ref={searchableContent}
                onScroll={updateFades}
                className="h-full max-h-96 overflow-y-auto gap-4 flex flex-col"
              >
                {transcriptSegments.length === 0 ? (
                  <p className="text-spk-mediumContrast text-sm italic py-4 text-center">
                    No transcript available.
                  </p>
                ) : (
                  transcriptSegments.map((segment, index) => {
                    const segmentKey =
                      segment.startMs !== undefined
                        ? `seg-${segment.startMs}`
                        : `seg-${segment.text.slice(0, 20)}-${index}`;
                    const baseClass = `flex justify-between items-start gap-2 p-4 rounded-xl border border-solid max-w-full text-spk-mediumContrast ${
                      index % 2 === 0
                        ? 'bg-spk-neutral-20 border-spk-neutral-40'
                        : 'bg-white border-spk-softBlue110'
                    }`;
                    const inner = (
                      <>
                        <span className="break-words min-w-0">
                          {segment.text}
                        </span>
                        {segment.startMs !== undefined && (
                          <span className="text-xs opacity-60 flex-shrink-0 ml-2 mt-0.5">
                            {formatTime(segment.startMs)}
                          </span>
                        )}
                      </>
                    );

                    return segment.startMs !== undefined ? (
                      <button
                        key={segmentKey}
                        type="button"
                        className={`w-full text-left ${baseClass} hover:opacity-75 transition-opacity`}
                        onClick={() => handleSeek(segment.startMs!)}
                      >
                        {inner}
                      </button>
                    ) : (
                      <div key={segmentKey} className={baseClass}>
                        {inner}
                      </div>
                    );
                  })
                )}
              </div>
              {showBottomFade && (
                <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-10 z-10 bg-gradient-to-t from-white to-transparent" />
              )}
            </div>
          </div>
        )}
      </div>
      {footer && (
        <div className="flex flex-row justify-between items-center mt-4">
          {footer}
        </div>
      )}
    </>
  );
};

SmartInterviewVideoModal.defaultProps = {
  video: undefined,
  footer: undefined,
};

export default SmartInterviewVideoModal;
